import axios from '@/axiosInstance.ts'
import {parseResponseData} from '@/axiosInstance'
import {captureException} from '@sentry/react'
import {EventTypeKeyName} from '@/features/agenda/types'
import {z} from 'zod'
import {PaginationParams, PaginationResponse, ResponseType, TableFiltersPayload, TableOrder} from '@/types'
import {Absences, AccessesType, AccessRateCode} from '../types'
import {Access, AccessesDetail} from '@/features/accesses/types'
import {AccessGroupNameEnum, AccessType, AccessTypeKeyName} from '@/features/access-types/types'
import {buildFile, downloadFile, raise} from '@/utilities/helpers'
import dayjs from 'dayjs'
import {TIMEOUT_EXPORT_ACCESSES} from '../utils'
import {ExportJob} from '@/features/exports/types'

export const HttpGetAccessesDetailOptions = z.object({
    params: z.object({
        projectId: z.string(),
        responseType: ResponseType
    })
})
export type HttpGetAccessesDetailOptions = z.infer<typeof HttpGetAccessesDetailOptions>

export const HttpGetAccessesDetailResponse = z.object({
    in_person_accesses: z.array(AccessesDetail.extend({accessType: EventTypeKeyName, accessName: z.string()})),
    non_in_person_accesses: z.array(AccessesDetail.extend({accessType: EventTypeKeyName, accessName: z.string()})),
    treatments: z.array(AccessesDetail.extend({accessType: EventTypeKeyName, accessName: z.string()})),
    unaccounted_accesses: z.array(AccessesDetail.extend({accessType: EventTypeKeyName, accessName: z.string()}))
})
export type HttpGetAccessesDetailResponse = z.infer<typeof HttpGetAccessesDetailResponse>

export const httpGetAccessesDetail = async ({params}: HttpGetAccessesDetailOptions) => {
    const response = await axios.get(`accesses/detail`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetAccessesDetailResponse
    })
}

export interface HttpGetAbsencesOptions {
    params: PaginationParams & {
        projectId?: string
        startDate?: string
        endDate?: string
    }
}

export const HttpGetAbsencesResponse = z.object({
    data: z.array(Absences),
    meta: PaginationResponse
})
export type HttpGetAbsencesResponse = z.infer<typeof HttpGetAbsencesResponse>

export const httpGetAbsences = async ({params}: HttpGetAbsencesOptions) => {
    const response = await axios.get(`projects/${params.projectId}/absences`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetAbsencesResponse
    })
}

export interface HttpGetAccessesTableOptions {
    params: PaginationParams & {
        search?: string
        startDate?: string
        endDate?: string
    }
    payload?: TableFiltersPayload & {
        sorts?: TableOrder[]
    }
}

export const HttpGetAccessesResponse = z.object({
    data: z.array(Access),
    meta: PaginationResponse
})
export type HttpGetAccessesResponse = z.infer<typeof HttpGetAccessesResponse>

export const httpGetAccessesTable = async ({params, payload}: HttpGetAccessesTableOptions) => {
    const response = await axios.post('accesses/index', payload, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetAccessesResponse
    })
}

export interface HttpGetAccessesOptions {
    params: PaginationParams & {
        search?: string
        medicalRecordReportType?: string
        patient?: string
        hasReport?: boolean
        project?: string
        accessType?: AccessTypeKeyName
        type?: AccessesType
    }
}

export const httpGetAccesses = async ({params}: HttpGetAccessesOptions) => {
    const response = await axios.get('accesses', {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetAccessesResponse
    })
}

interface HttpGetAccessOptions {
    urlParams: {
        accessId: string
    }
}

export const httpGetAccess = async ({urlParams}: HttpGetAccessOptions) => {
    const response = await axios.get(`accesses/${urlParams.accessId}`)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: Access
    })
}

export interface HttpValidateAccessesOptions {
    payload: {
        accessIds: string[]
    }
}

export const httpValidateAccesses = ({payload}: HttpValidateAccessesOptions) => {
    return axios.post(`accesses/validate`, payload)
}

export interface HttpAccountAccessesOptions {
    payload: {
        accessIds: string[]
    }
}

export const httpAccountAccesses = ({payload}: HttpAccountAccessesOptions) => {
    return axios.post(`accesses/accounts`, payload)
}

export interface HttpDiscardAccessesOptions {
    payload: {
        accessIds: string[]
    }
}

export const httpDiscardAccesses = ({payload}: HttpDiscardAccessesOptions) => {
    return axios.post(`accesses/discard`, payload)
}
export interface HttpDeleteAccessesOptions {
    urlParams: {
        accessId: string
    }
}

export const httpDeleteAccesses = ({urlParams}: HttpDeleteAccessesOptions) => {
    return axios.delete(`accesses/${urlParams.accessId}`)
}

export const HttpCreateAccessOptions = z.object({
    payload: z.array(
        z.object({
            patientId: z.string(),
            projectId: z.string(),
            consultantId: z.string(),
            accessTypeId: z.string(),
            type: z.string(),
            rateCode: AccessRateCode.nullable(),
            isARecovery: z.boolean().optional(),
            isRemote: z.boolean().optional(),
            missedAccessId: z.string().nullish(),
            attachmentId: z.string().nullish(),
            sector: z.string(),
            date: z.string(),
            time: z.string().optional(),
            duration: z.number(),
            communicatedOnTime: z.boolean().optional()
        })
    )
})
export type HttpCreateAccessOptions = z.infer<typeof HttpCreateAccessOptions>

export const httpCreateAccess = ({payload}: HttpCreateAccessOptions) => {
    return axios.post('accesses', payload)
}

export const HttpUpdateAccessOptions = z.object({
    urlParams: z.object({
        accessId: z.string().min(1).optional()
    }),
    payload: z.object({
        patientId: z.string().optional(),
        projectId: z.string().optional(),
        consultantId: z.string().optional(),
        accessTypeId: z.string().optional(),
        type: z.string().optional(),
        rateCode: AccessRateCode.nullish(),
        isARecovery: z.boolean().optional(),
        isRemote: z.boolean().optional(),
        missedAccessId: z.string().nullish(),
        attachmentId: z.string().nullish(),
        sector: z.string().optional(),
        date: z.string().optional(),
        time: z.string().optional(),
        duration: z.number().optional(),
        communicatedOnTime: z.boolean().optional(),
        status: Access.shape.status.optional()
    })
})
export type HttpUpdateAccessOptions = z.infer<typeof HttpUpdateAccessOptions>

export const httpUpdateAccess = ({urlParams, payload}: HttpUpdateAccessOptions) => {
    return axios.patch(`accesses/${urlParams.accessId}`, payload)
}

export const DashboardCard = z.enum([
    'treatments',
    'absences',
    'in_person_accesses',
    'non_in_person_accesses',
    'to_validate_accesses'
])
export type DashboardCard = z.infer<typeof DashboardCard>

// Definition of individual schemas for each card
const TreatmentsResponse = z.object({
    treatments: z.object({
        done: z.number(),
        planned: z.number(),
        difference: z.number().optional()
    })
})

const AbsencesResponse = z.object({
    absences: z.object({
        detected: z.number(),
        toValidate: z.number().optional(),
        treatments: z.number().optional()
    })
})

const InPersonAccessesResponse = z.object({
    inPersonAccesses: z.object({
        done: z.number(),
        planned: z.number()
    })
})

const NonInPersonAccessesResponse = z.object({
    nonInPersonAccesses: z.object({
        done: z.number(),
        planned: z.number()
    })
})

const ToValidateAccessesResponse = z.object({
    toValidateAccesses: z.number()
})

// Map of response types for each card
const DashboardResponseSchemas = {
    treatments: TreatmentsResponse,
    absences: AbsencesResponse,
    in_person_accesses: InPersonAccessesResponse,
    non_in_person_accesses: NonInPersonAccessesResponse,
    to_validate_accesses: ToValidateAccessesResponse
} as const

// Conditional type for response based on card parameter
export type HttpGetAccessesDashboardResponse<T extends DashboardCard> = T extends 'treatments'
    ? z.infer<typeof TreatmentsResponse>
    : T extends 'absences'
      ? z.infer<typeof AbsencesResponse>
      : T extends 'in_person_accesses'
        ? z.infer<typeof InPersonAccessesResponse>
        : T extends 'non_in_person_accesses'
          ? z.infer<typeof NonInPersonAccessesResponse>
          : T extends 'to_validate_accesses'
            ? z.infer<typeof ToValidateAccessesResponse>
            : never

export interface HttpGetAccessesDashboardOptions<T extends DashboardCard = DashboardCard> {
    payload?: TableFiltersPayload & {
        sorts?: TableOrder[]
    }
    params?: {
        card: T
        startDate?: string
        endDate?: string
    }
}

// Complete schema for validation (kept for compatibility)
export const HttpGetAccessesDashboardResponse = z.object({
    treatments: TreatmentsResponse,
    absences: AbsencesResponse,
    inPersonAccesses: InPersonAccessesResponse,
    nonInPersonAccesses: NonInPersonAccessesResponse,
    toValidateAccesses: ToValidateAccessesResponse
})
export type HttpGetAccessesDashboardResponseFull = z.infer<typeof HttpGetAccessesDashboardResponse>

// Generic function for calls with specific card
export const httpGetAccessesDashboard = async <T extends DashboardCard>({
    payload,
    params
}: HttpGetAccessesDashboardOptions<T>): Promise<HttpGetAccessesDashboardResponse<T>> => {
    const response = await axios.post(`accesses/dashboard`, payload, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: DashboardResponseSchemas[params?.card ?? raise('Card parameter is required for typed response')]
    }) as Promise<HttpGetAccessesDashboardResponse<T>>
}

// Function for calls without specific card (returns complete type)
export const httpGetAccessesDashboardFull = async ({
    payload,
    params
}: Omit<HttpGetAccessesDashboardOptions, 'params'> & {
    params?: {startDate?: string; endDate?: string}
}): Promise<HttpGetAccessesDashboardResponseFull> => {
    const response = await axios.post(`accesses/dashboard`, payload, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetAccessesDashboardResponse
    })
}

/*Export accesses*/
interface HttpExportAccessesOptions {
    params: {
        startDate: string
        endDate: string
    }
    payload: {
        selectedAccessIds: string[]
        exceptedAccessIds: string[]
        type: 'xlsx' | 'xls' | 'csv'
        filters?: TableFiltersPayload['filters']
        fields?: string[]
        sorts?: TableOrder[]
    }
}
/* Async: the (deprecated) endpoint now enqueues a job and responds 202 with the ExportJob. It is kept
   instead of POSTing /v1/exports directly because the accesses export is scoped by a startDate/endDate
   period (sent as query params) that the generic /v1/exports payload does not model. */
export const httpExportAccesses = ({payload, params}: HttpExportAccessesOptions) =>
    axios.post('accesses/export', payload, {
        params,
        timeout: TIMEOUT_EXPORT_ACCESSES
    })

interface HttpExportAccessesOptions {
    payload: {
        selectedAccessIds: string[]
        exceptedAccessIds: string[]
        type: 'xlsx' | 'xls' | 'csv'
        filters?: TableFiltersPayload['filters']
        fields?: string[]
        sorts?: TableOrder[]
    }
}
interface HttpExportAccessesReportOptions {
    params: {
        startDate: string
        endDate: string
    }
    payload: {
        selectedAccessIds: string[]
        exceptedAccessIds: string[]
        startDate: string
        endDate: string
        type: 'xlsx' | 'xls' | 'csv' | 'txt'
        filters?: TableFiltersPayload['filters']
    }
}
/* Async for xlsx/xls/csv (202 + ExportJob). The `txt` format is handled by httpExportAccessesTxt
   (a synchronous DSP-flux ZIP download), so it never reaches this function. */
export const httpExportAccessesReport = ({payload, params}: HttpExportAccessesReportOptions) =>
    axios.post('accesses/export-report', payload, {params, timeout: TIMEOUT_EXPORT_ACCESSES})

export interface HttpExportAccessesRiaPayload {
    year: number
    month?: number
    format: 'csv' | 'pdf'
}
/* The export is requested for an accounting period (month+year, or year only) rather than a
   selection of accesses. Async for both csv and pdf (202 + ExportJob). */
export const httpExportAccessesRia = async (payload: HttpExportAccessesRiaPayload) => {
    const response = await axios.post('accesses/export-ria', payload, {timeout: TIMEOUT_EXPORT_ACCESSES})
    return parseResponseData({responseData: response.data, onZodError: captureException, responseShape: ExportJob})
}

export interface HttpExportAccessesTxtOptions {
    payload: {
        startDate: string
        endDate: string
        selectedAccessIds: string[]
        exceptedAccessIds: string[]
    }
}
export const httpExportAccessesTxt = ({payload}: HttpExportAccessesTxtOptions) =>
    axios
        .post('accesses/export-pri-txt', payload, {responseType: 'blob', timeout: TIMEOUT_EXPORT_ACCESSES})
        .then(response => {
            const zipFile = buildFile({
                extension: 'zip',
                fileName: `dsp-flux-${dayjs().format('DD-MM-YYYY')}`,
                data: response.data
            })
            downloadFile(zipFile)
            return response.data
        })

export const HttpGetSignatureSheetsOptions = z.object({
    params: z.object({
        consultant: z.string(),
        patient: z.string().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional()
    })
})
export type HttpGetSignatureSheetsOptions = z.infer<typeof HttpGetSignatureSheetsOptions>

export const HttpGetSignatureSheetsResponse = z.array(
    z.object({
        period: z.string(),
        patientFullName: z.string(),
        priCode: z.number(),
        numberOfTreatments: z.number(),
        numberOfOtherTreatments: z.number(),
        numberOfAbsences: z.number(),
        globalStatus: Access.shape.status,
        accesses: z.array(Access)
    })
)
export type HttpGetSignatureSheetsResponse = z.infer<typeof HttpGetSignatureSheetsResponse>

export const httpGetSignatureSheets = async ({params}: HttpGetSignatureSheetsOptions) => {
    const response = await axios.get('accesses/signature-sheets', {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetSignatureSheetsResponse
    })
}

export interface HttpGetLastAccessOptions {
    params: {
        projectId: string
        groupName: AccessGroupNameEnum
    }
}

export const HttpGetLastAccessResponse = z.object({
    date: z.string(),
    accessType: AccessType
})
export type HttpGetLastAccessResponse = z.infer<typeof HttpGetLastAccessResponse>

export const httpGetLastAccess = async ({params}: HttpGetLastAccessOptions) => {
    const response = await axios.get('accesses/last-access', {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetLastAccessResponse
    })
}
